import React from 'react';
import { Box, Button, Stack } from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { TabsContainer } from 'components/shared/TabsContainer/TabsContainer';

import { RulesSection } from './RulesSection';
import { CommonSection } from './CommonSection';
import { ChargesSection } from './ChargesSection';
import { TaxesSection } from './TaxesSection';
import { useChargesConfiguration } from '../../api';
import { dealSheetConfigurationMapper } from '../../mappers';
import { DealSheetFormValues, salesCenterFormSchema } from './types';

interface Props {
  id: string | null;
  onCancel: VoidFunction;
  isConfigLoading: boolean;
  initialValues: DealSheetFormValues;
}

export const DealSheetRuleForm = ({ id, initialValues, onCancel }: Props) => {
  const { isConfigurationCreating, handleCreateConfiguration, handleUpdateConfiguration, isConfigurationUpdating } =
    useChargesConfiguration();

  const form = useForm<DealSheetFormValues>({
    mode: 'all',
    defaultValues: initialValues,
    resolver: zodResolver(salesCenterFormSchema),
  });

  const handleSubmitDealRules = async (formData: DealSheetFormValues) => {
    const { name, description, charges, taxes, address, ...rules } = formData;
    const { country, ...addressRules } = address;
    const hasRules = hasAnyValue(rules) || hasAnyValue(addressRules);

    if (!hasRules) {
      form.setError('root', { message: 'At least one filter must be selected' });
      return;
    }

    const dto = dealSheetConfigurationMapper.processSalesCenterFormToAPIDto(formData);

    if (id) {
      await handleUpdateConfiguration({ ...dto, id }, api => ({
        refetchQueries: [api.getConfigs],
        awaitRefetchQueries: true,
      }));
    } else {
      await handleCreateConfiguration(dto, api => ({
        refetchQueries: [api.getConfigs],
        awaitRefetchQueries: true,
      }));
    }

    form.reset();

    onCancel();
  };

  return (
    <FormProvider {...form}>
      <Stack className="salesCenterCard" gap="1rem" height="100%" padding="0 0.5rem">
        <CommonSection />

        <Box height="100%">
          <TabsContainer
            tabs={[
              { label: 'Conditions', value: 'common', component: <RulesSection /> },
              { label: 'Charges & Fees', value: 'charges', component: <ChargesSection /> },
              { label: 'Taxes', value: 'tax', component: <TaxesSection /> },
            ]}
          />
        </Box>

        <Stack direction="row" gap="0.5rem">
          <Button
            variant="contained"
            onClick={form.handleSubmit(handleSubmitDealRules, console.log)}
            loading={form.formState.isSubmitting || isConfigurationCreating || isConfigurationUpdating}
          >
            Save Rule
          </Button>

          <Button color="error" onClick={() => form.reset()} disabled={isConfigurationCreating}>
            Reset
          </Button>

          <Button onClick={onCancel} disabled={isConfigurationCreating}>
            Cancel
          </Button>
        </Stack>
      </Stack>
    </FormProvider>
  );
};

function hasAnyValue(obj: unknown): boolean {
  if (Array.isArray(obj)) return obj.length > 0;

  if (obj && typeof obj === 'object') {
    return Object.values(obj).some(hasAnyValue);
  }

  return false;
}
